import { useRef, useState, useEffect } from "react"
import { useSelector } from 'react-redux'
import { WeekOfTheYearKey } from 'classes/WeekOfTheYearKey'
import moment from 'moment'

export default function PresentWeekHighlighter({
    calendarRef,
    calendarContainerRef,
    calendarEvents,
}) {
    const presentWeekOverlayRef = useRef(null)
    const [presentWeekOverlayStyle, setPresentWeekOverlayStyle] = useState({ display: 'none' })

    const trainingChartPresentWeekOfTheYearKey = useSelector(state => state.athleteProfile.trainingChartPresentWeekOfTheYearKey)
    const trainingChartSelectedWeekOfTheYearKey = useSelector(state => state.athleteProfile.trainingChartSelectedWeekOfTheYearKey)

    const UpdatePresentWeekOverlay = () => {

        if (!calendarRef.current || !presentWeekOverlayRef.current) {
            setPresentWeekOverlayStyle({ display: 'none' })
            return
        }

        const calendarEl = calendarRef.current.getApi()?.el
        if (!calendarEl) {
            setPresentWeekOverlayStyle({ display: 'none' })
            return
        }

        // Find the scrollable container
        const scroller = calendarEl.querySelector('.fc-scroller')
        if (!scroller) {
            setPresentWeekOverlayStyle({ display: 'none' })
            return
        }

        // Find the tbody with week rows
        const tbody = calendarEl.querySelector('tbody[role="presentation"]')
        if (!tbody) {
            setPresentWeekOverlayStyle({ display: 'none' })
            return
        }

        // Get the present week start date
        const presentWeekKey = new WeekOfTheYearKey(trainingChartPresentWeekOfTheYearKey)
        const presentWeekStartDate = presentWeekKey.toDate().format('YYYY-MM-DD')

        // Find the row containing the present week
        const weekRows = tbody.querySelectorAll('tr[role="row"]')
        let presentWeekRow = null

        for (let row of weekRows) {
            const dayCells = row.querySelectorAll('td[data-date]')
            for (let cell of dayCells) {
                const cellDate = cell.getAttribute('data-date')
                if (cellDate === presentWeekStartDate) {
                    presentWeekRow = row
                    break
                }
            }
            if (presentWeekRow) break
        }

        if (!presentWeekRow) {
            setPresentWeekOverlayStyle({ display: 'none' })
            return
        }

        // Get the first and last day cells of the present week
        const dayCells = presentWeekRow.querySelectorAll('td[data-date]')
        const presentWeekCells = Array.from(dayCells).filter(cell => {
            const cellDate = cell.getAttribute('data-date')
            const cellWeekKey = new WeekOfTheYearKey(moment(cellDate))
            return cellWeekKey.equals(presentWeekKey)
        })

        if (presentWeekCells.length === 0) {
            setPresentWeekOverlayStyle({ display: 'none' })
            return
        }

        // Get bounding rects
        const firstCellRect = presentWeekCells[0].getBoundingClientRect()
        const lastCellRect = presentWeekCells[presentWeekCells.length - 1].getBoundingClientRect()
        
        if (!calendarContainerRef.current) {
            setPresentWeekOverlayStyle({ display: 'none' })
            return
        }
        
        const containerRect = calendarContainerRef.current.getBoundingClientRect()

        // Calculate position relative to calendar container (which is the parent of the overlay)
        const left = firstCellRect.left - containerRect.left - 1
        const top = firstCellRect.top - containerRect.top
        const width = lastCellRect.right - firstCellRect.left + 34.5 // Add w-8 (32px) to include AthleteCalendarProgramWeeks
        const height = firstCellRect.height

        // Edge case where highligher is at the bottom of the calendar

        var threeWeeksFromPresentWeek = new WeekOfTheYearKey(trainingChartPresentWeekOfTheYearKey).add(-3)

        if (new WeekOfTheYearKey(trainingChartSelectedWeekOfTheYearKey).equals(threeWeeksFromPresentWeek)) {
            // console.log('Add custom styling now!!')

            setPresentWeekOverlayStyle({
                display: 'block',
                position: 'absolute',
                left: `${left}px`,
                top: `${top}px`,
                width: `${width}px`,
                height: `${height - 1}px`, // Decrease height by 1px
                pointerEvents: 'none',
                zIndex: 1,
                borderBottomRightRadius: '15px' // Rounded bottom right corner
            })
            
            return
        }

        // Normal styling

        setPresentWeekOverlayStyle({
            display: 'block',
            position: 'absolute',
            left: `${left}px`,
            top: `${top}px`,
            width: `${width}px`,
            height: `${height}px`,
            pointerEvents: 'none',
            zIndex: 1
        })

    }

    useEffect(() => {
        // Use requestAnimationFrame for immediate smooth updates
        const scheduleUpdate = () => {
            requestAnimationFrame(() => {
                requestAnimationFrame(UpdatePresentWeekOverlay)
            })
        }

        // Initial update - try immediately, then retry if needed
        scheduleUpdate()

        // Update on scroll and resize
        const calendarEl = calendarRef.current?.getApi()?.el
        const scroller = calendarEl?.querySelector('.fc-scroller')
        
        // Use requestAnimationFrame for smooth scroll updates
        const handleScroll = () => {
            requestAnimationFrame(UpdatePresentWeekOverlay)
        }
        
        // Update on window resize
        const handleResize = () => {
            requestAnimationFrame(UpdatePresentWeekOverlay)
        }
        
        if (scroller) {
            scroller.addEventListener('scroll', handleScroll, { passive: true })
        }
        window.addEventListener('resize', handleResize, { passive: true })

        // Update when calendar events change - use minimal delay for DOM to settle
        const timeoutId = setTimeout(scheduleUpdate, 16) // ~1 frame at 60fps
        
        // Fallback update in case calendar takes longer to render
        const renderTimeoutId = setTimeout(scheduleUpdate, 50)

        return () => {
            if (scroller) {
                scroller.removeEventListener('scroll', handleScroll)
            }
            window.removeEventListener('resize', handleResize)
            clearTimeout(timeoutId)
            clearTimeout(renderTimeoutId)
        }
    }, [calendarRef, trainingChartPresentWeekOfTheYearKey, calendarEvents, trainingChartSelectedWeekOfTheYearKey])

    return (
        <div
            ref={presentWeekOverlayRef}
            style={presentWeekOverlayStyle}
            className="border-2 border-orange-300 pointer-events-none"
        />
    )
}